import { useEffect, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { HIDDEN_TEXT, captionFor, elapsedSince, formatDuration } from './workedTime';

// Ticks once a second, but ONLY while `active` - a finished day, or a person who has not punched in, costs nothing.
const useNow = (active) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
};

// A person's worked time today, in the Attendance page's style: a small line ("Working since 09:30 AM" / "09:30 AM - 06:15 PM") above
// a large tabular HH:MM:SS - ticking while they are still in, green once they have punched out. `worked` is { state, firstIn, lastOut,
// workedSeconds } from the server; `large` is the full-size version for "your time" at the top, the default is the compact table cell.
const WorkedTimer = ({ worked, large = false, loading = false }) => {
  const working = worked?.state === 'working';
  const now = useNow(working);
  const faint = { fontSize: large ? '0.85rem' : '0.8rem', color: 'text.secondary' };

  if (!worked) return <Typography component="span" sx={faint}>{loading ? '…' : '-'}</Typography>;
  if (worked.state === 'hidden') {
    return (
      <Tooltip title={HIDDEN_TEXT} placement="top">
        <Typography component="span" aria-label={HIDDEN_TEXT} sx={{ ...faint, cursor: 'help' }}>-</Typography>
      </Tooltip>
    );
  }
  if (worked.state === 'no_biometric_id') return <Typography component="span" sx={faint}>No biometric ID</Typography>;
  if (worked.state === 'not_in') return <Typography component="span" sx={faint}>Not punched in yet</Typography>;

  const done = worked.state === 'done';
  const seconds = done ? worked.workedSeconds : elapsedSince(worked.firstIn, now);
  return (
    <Box data-testid="worked-timer" data-state={worked.state} sx={{ display: 'flex', flexDirection: 'column', alignItems: large ? 'center' : 'flex-end', lineHeight: 1.25 }}>
      <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>{captionFor(worked)}</Box>
      <Box
        component="span"
        aria-label={done ? 'Worked time' : 'Time worked so far'}
        sx={{ fontSize: large ? { xs: '1.1rem', md: '1.4rem' } : '1rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: done ? 'success.main' : 'text.primary' }}
      >
        {formatDuration(seconds)}
      </Box>
    </Box>
  );
};

export default WorkedTimer;
